const SW_URL = "/firebase-messaging-sw.js";
const DISMISS_STORAGE_KEY = "mfg_pwa_install_dismissed_at";
const DISMISS_MS = 14 * 24 * 60 * 60 * 1000;
const INSTALL_AVAILABLE_EVENT = "mfg:pwa-install-available";
const INSTALLED_EVENT = "mfg:pwa-installed";

const HIDDEN_PATHS = [
  "/informationAccount",
  "/personalInformation",
  "/registrationEmail",
  "/device-verification",
  "/agreement",
];

let serviceWorkerPromise = null;

const isBrowser = () => typeof window !== "undefined";

export const isStandaloneDisplay = () => {
  if (!isBrowser()) return false;
  const mediaStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches;
  const mediaMinimal = window.matchMedia?.("(display-mode: minimal-ui)")?.matches;
  const iosStandalone = Boolean(window.navigator?.standalone);
  return Boolean(mediaStandalone || mediaMinimal || iosStandalone);
};

export const isIosDevice = () => {
  if (!isBrowser()) return false;
  const ua = window.navigator.userAgent || "";
  const iOS = /iphone|ipad|ipod/i.test(ua);
  const iPadOs = window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1;
  return iOS || iPadOs;
};

export const isIosSafari = () => {
  if (!isIosDevice()) return false;
  const ua = window.navigator.userAgent || "";
  const isSafari = /safari/i.test(ua) && !/crios|fxios|edgios|opr\//i.test(ua);
  return isSafari;
};

export const isMobileDevice = () => {
  if (!isBrowser()) return false;
  const uaData = window.navigator.userAgentData;
  if (typeof uaData?.mobile === "boolean" && uaData.mobile) {
    return true;
  }
  if (isIosDevice()) return true;
  const ua = window.navigator.userAgent || "";
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/i.test(ua);
};

export const isDesktopDevice = () => !isMobileDevice();

export const shouldHideInstallOnPath = (pathname = "") =>
  HIDDEN_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

export const isInstallPromptDismissed = () => {
  if (!isBrowser()) return false;
  try {
    const raw = window.localStorage.getItem(DISMISS_STORAGE_KEY);
    const dismissedAt = Number(raw);
    if (!Number.isFinite(dismissedAt) || dismissedAt <= 0) return false;
    return Date.now() - dismissedAt < DISMISS_MS;
  } catch (_) {
    return false;
  }
};

export const dismissInstallPrompt = () => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now()));
  } catch (_) {}
};

export const clearInstallPromptDismissal = () => {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(DISMISS_STORAGE_KEY);
  } catch (_) {}
};

export const getDeferredInstallPrompt = () => {
  if (!isBrowser()) return null;
  return window.__mfgDeferredInstallPrompt || null;
};

export const promptPwaInstall = async () => {
  if (!isBrowser()) return { ok: false, reason: "unavailable" };
  if (isStandaloneDisplay()) return { ok: false, reason: "installed" };

  const promptEvent = getDeferredInstallPrompt();
  if (!promptEvent) {
    if (isIosSafari() || isIosDevice()) return { ok: false, reason: "ios" };
    return { ok: false, reason: "unavailable" };
  }

  try {
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    window.__mfgDeferredInstallPrompt = null;
    window.dispatchEvent(new CustomEvent(INSTALL_AVAILABLE_EVENT));
    if (choice?.outcome === "accepted") {
      return { ok: true, outcome: "accepted" };
    }
    return { ok: false, reason: "dismissed", outcome: choice?.outcome };
  } catch (_) {
    window.__mfgDeferredInstallPrompt = null;
    window.dispatchEvent(new CustomEvent(INSTALL_AVAILABLE_EVENT));
    return { ok: false, reason: "error" };
  }
};

export const registerPwaServiceWorker = () => {
  if (!isBrowser() || !("serviceWorker" in navigator)) {
    return Promise.resolve(null);
  }
  if (!serviceWorkerPromise) {
    serviceWorkerPromise = navigator.serviceWorker.register(SW_URL).catch(() => null);
  }
  return serviceWorkerPromise;
};

const markStandaloneClass = () => {
  if (!isBrowser() || typeof document === "undefined") return;
  if (isStandaloneDisplay()) {
    document.documentElement.classList.add("mfg-pwa-standalone");
  }
};

const captureInstallPrompt = (event) => {
  event.preventDefault();
  window.__mfgDeferredInstallPrompt = event;
  window.dispatchEvent(new CustomEvent(INSTALL_AVAILABLE_EVENT));
};

const handleAppInstalled = () => {
  window.__mfgDeferredInstallPrompt = null;
  clearInstallPromptDismissal();
  document.documentElement.classList.add("mfg-pwa-standalone");
  window.dispatchEvent(new CustomEvent(INSTALLED_EVENT));
};

export const initPwaInstall = () => {
  if (!isBrowser()) return;

  markStandaloneClass();
  registerPwaServiceWorker();

  window.addEventListener("beforeinstallprompt", captureInstallPrompt);
  window.addEventListener("appinstalled", handleAppInstalled);
};

export { INSTALL_AVAILABLE_EVENT, INSTALLED_EVENT };
